import React, { FC, useState } from 'react';
import { Form, Formik } from 'formik';
import { object, ref, string } from 'yup';

// Models
import { PowerOfAttorneyFormValue } from '../../../models/Forms/PowerOfAttorneyFormValue';

// i18n
import { useTranslation } from '../../../translations/useTranslation';

// Components
import { Button } from '../../Button';
import { FormikDropdown, FormikInput } from '../../formik';
import { Icon, Modal } from '../../common';

// Utils
import { getCgiDropdownOptions } from '../../../utilities/cgi';

interface Props {
  formValues: PowerOfAttorneyFormValue;
  onSubmit: (values: PowerOfAttorneyFormValue) => Promise<void>;
  modalContent?: string;
  information?: string;
}

const PowerOfAttorneyForm: FC<Props> = ({
  formValues,
  onSubmit,
  modalContent,
  information,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t, m, keys } = useTranslation();
  const signOptions = getCgiDropdownOptions(t, keys);
  const validationSchema = object().shape({
    fullName: string().required(t(keys.errorRequired)),
    email: string()
      .required(t(keys.errorRequired))
      .email(t(keys.errorInvalidEmail)),
    emailRepeated: string()
      .required(t(keys.errorRequired))
      .oneOf(
        [ref('email')],
        m(
          keys.errorMismatch,
          '{otherField}',
          t(keys.formEmailAddress).toLocaleLowerCase()
        )
      ),
    phone: string().required(t(keys.errorRequired)),
  });

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  return (
    <>
      <h2 className="form-app__heading">{t(keys.powerOfAttorneyFormTitle)}</h2>
      <Formik
        initialValues={formValues}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, submitForm }) => (
          <Form>
            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.uiPersonalInformation)}</legend>
              <FormikInput
                label={`${t(keys.formName)} *`}
                type="text"
                name="fullName"
                placeholder={t(keys.formFullName)}
              />
              <FormikInput
                label={`${t(keys.formEmailAddress)} *`}
                type="text"
                name="email"
              />
              <FormikInput
                label={`${m(
                  keys.formRepeat,
                  '{fieldName}',
                  t(keys.formEmailAddress).toLowerCase()
                )} *`}
                type="text"
                name="emailRepeated"
              />
              <FormikInput
                label={`${t(keys.formPhoneNumber)} *`}
                type="text"
                name="phone"
              />
            </fieldset>
            <fieldset className="aea-form-fieldset">
              <legend>
                <div className="flex items-center">
                  <div className="inline-block">
                    {t(keys.uiPowerOfAttorney)}
                  </div>
                  {modalContent && (
                    <>
                      <button
                        className="button-reset flex inline-block tooltip-icon-distance"
                        type="button"
                        onClick={toggleModal}
                      >
                        <Icon
                          type="info"
                          className="aea-form-icon aea-form-icon--sm color-green-dark"
                        />
                      </button>

                      <Modal show={modalOpen} onClose={toggleModal}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: modalContent,
                          }}
                        ></div>
                      </Modal>
                    </>
                  )}
                </div>
              </legend>
              {information && (
                <div dangerouslySetInnerHTML={{ __html: information }}></div>
              )}
              <FormikDropdown
                name="authenticationOption"
                label={`${t(keys.formSelectAuthenticationOption)} *`}
                options={signOptions}
              />
            </fieldset>
            <Button
              title={t(keys.uiSubmit)}
              onClick={submitForm}
              disabled={!isValid}
              isLoading={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PowerOfAttorneyForm;
